<script setup lang="ts">
import { ref } from 'vue';

const props = withDefaults(defineProps<{
  text: string;
  position?: 'top' | 'bottom' | 'left' | 'right';
  disabled?: boolean;
}>(), {
  position: 'top',
  disabled: false,
});

const isHovered = ref(false);

function handleMouseEnter() {
  if (!props.disabled) {
    isHovered.value = true;
  }
}

function handleMouseLeave() {
  isHovered.value = false;
}
</script>

<template>
  <div class="tooltip-wrapper"
       @mouseenter="handleMouseEnter"
       @mouseleave="handleMouseLeave">
    <slot />
    <transition name="tooltip-fade">
      <div v-if="isHovered && text"
           class="tooltip"
           :class="`tooltip_${position}`"
           role="tooltip">
        {{ text }}
        <div class="tooltip-arrow"></div>
      </div>
    </transition>
  </div>
</template>

<style lang="scss" scoped>
.tooltip-wrapper {
  position: relative;
  display: inline-block;
}

.tooltip {
  position: absolute;
  padding: 0.75rem 1rem;
  background-color: rgba(0, 0, 0, 0.9);
  color: white;
  border-radius: 0.375rem;
  font-size: 14px;
  white-space: pre-wrap;
  line-height: 1.4;
  max-width: 300px;
  width: max-content;
  z-index: 1000;
  pointer-events: none;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);

  &_top {
    bottom: calc(100% + 0.5rem);
    left: 50%;
    transform: translateX(-50%);
  }

  &_bottom {
    top: calc(100% + 0.5rem);
    left: 50%;
    transform: translateX(-50%);
  }

  &_left {
    right: calc(100% + 0.5rem);
    top: 50%;
    transform: translateY(-50%);
  }

  &_right {
    left: calc(100% + 0.5rem);
    top: 50%;
    transform: translateY(-50%);
  }
}

.tooltip-arrow {
  position: absolute;
  width: 0;
  height: 0;
  border: 6px solid transparent;

  .tooltip_top & {
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border-top-color: rgba(0, 0, 0, 0.9);
  }

  .tooltip_bottom & {
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    border-bottom-color: rgba(0, 0, 0, 0.9);
  }

  .tooltip_left & {
    left: 100%;
    top: 50%;
    transform: translateY(-50%);
    border-left-color: rgba(0, 0, 0, 0.9);
  }

  .tooltip_right & {
    right: 100%;
    top: 50%;
    transform: translateY(-50%);
    border-right-color: rgba(0, 0, 0, 0.9);
  }
}

.tooltip-fade-enter-active,
.tooltip-fade-leave-active {
  transition: opacity 0.2s ease-in-out;
}

.tooltip-fade-enter-from,
.tooltip-fade-leave-to {
  opacity: 0;
}

@media screen and (max-width: 1400px) {
  .tooltip {
    display: none;
  }
}
</style>